import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/wellmed", label: "WellMed" },
  { to: "/testimonials", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[var(--color-bg)]/80 backdrop-blur-2xl border-b border-[var(--color-border)] shadow-[0_1px_40px_rgba(0,0,0,0.4)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-[72px]">

        {/* LOGO */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">

          <div className="leading-tight">
            <div className="font-heading text-xl text-[var(--color-text)]">Dr. Ashish Gupta</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--color-coral)] font-semibold">
              Senior Interventional Cardiologist
            </div>
          </div>
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors duration-200 ${
                  isActive
                    ? "text-[var(--color-coral)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* DESKTOP ACTIONS */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${SITE.phone}`}
            data-testid="header-phone"
            className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-coral)] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="font-medium">{SITE.phoneDisplay}</span>
          </a>
          <Link
            to="/appointment"
            data-testid="header-book-appointment"
            className="btn-primary text-sm"
          >
            Book Appointment
          </Link>
        </div>

        {/* MOBILE TOGGLE */}
        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="menu"
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-coral)]/50 transition-colors"
        >
          {open
            ? <X className="w-5 h-5 text-[var(--color-text)]" />
            : <Menu className="w-5 h-5 text-[var(--color-text)]" />}
        </button>
      </div>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-[var(--color-bg)]/95 backdrop-blur-2xl border-t border-[var(--color-border)]"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-3 border-b border-[var(--color-border)] text-[15px] transition-colors ${
                        isActive ? "text-[var(--color-coral)]" : "text-[var(--color-text-muted)]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/appointment"
                data-testid="mobile-book-appointment"
                className="btn-primary mt-5 justify-center"
              >
                Book Appointment
              </Link>
              <a
                href={`tel:${SITE.phone}`}
                data-testid="mobile-phone"
                className="btn-outline mt-3 justify-center text-sm"
              >
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
